import * as React from 'react';

import useFormattedPhone from '../../hooks/useFormattedPhone';
import usePhoneVerification from '../../hooks/usePhoneVerification';
import Button from '../base/buttons/Button';
import { OTPInput } from '../base/Input';
import Modal from '../base/Modal';

interface IPhoneVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: () => void;
  phoneNumber: string;
}

const PhoneVerificationModal = ({ isOpen, onClose, onVerified, phoneNumber }: IPhoneVerificationModalProps) => {
  const {
    code,
    setCode,
    error,
    codeSent,
    isSending,
    isVerifying,
    secondsRemaining,
    isCountdownActive,
    handleSendCode,
    handleVerify,
    reset,
  } = usePhoneVerification(onVerified);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const formattedPhone = useFormattedPhone(phoneNumber);

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="sm">
      <Modal.Header onClose={onClose}>Verify phone number</Modal.Header>
      <Modal.Body className="gap-4 px-6 pb-6">
        {!codeSent ? (
          <>
            <div className="text-secondary text-sm">
              We'll send a verification code to <span className="text-primary font-medium">{formattedPhone}</span>.
            </div>
            <Button color="primary" size="md" className="w-full" onClick={handleSendCode} isLoading={isSending}>
              Send code
            </Button>
          </>
        ) : (
          <>
            <div className="text-secondary text-sm">
              Enter the code sent to <span className="text-primary font-medium">{formattedPhone}</span>.
            </div>
            <div className="flex justify-center">
              <OTPInput
                value={code}
                onChange={setCode}
                onComplete={(c) => void handleVerify(c)}
                isInvalid={!!error}
                errorMessage={error || undefined}
                isDisabled={isVerifying}
                autoFocus
              />
            </div>
            <Button
              color="primary"
              size="md"
              className="w-full"
              onClick={() => void handleVerify(code)}
              isLoading={isVerifying}
              isDisabled={code.length !== 6}
            >
              Verify
            </Button>
            <div className="flex justify-center">
              {isCountdownActive ? (
                <span className="text-tertiary text-sm">Resend code in {secondsRemaining}s</span>
              ) : (
                <Button color="link-color" size="sm" onClick={handleSendCode} isDisabled={isSending}>
                  Resend code
                </Button>
              )}
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PhoneVerificationModal;
